import { useState, useCallback } from 'react';
import { useApi } from '@/hooks/useApi';

interface OcrFields {
  fullName: string | null;
  dateOfBirth: string | null;
  gender: string | null;
  idNumber: string | null;
  idType: string | null;
  address: string | null;
  nationality: string | null;
  issueDate: string | null;
  expiryDate: string | null;
}

interface OcrData {
  rawText: string;
  fields: OcrFields;
  confidence: number;
  processedAt: string;
  engine: string;
}

interface TamperFlag {
  type: string;
  label: string;
  severity: 'low' | 'medium' | 'high';
}

interface TamperResult {
  compositeScore: number;
  riskLevel: 'low' | 'medium' | 'high';
  flags: TamperFlag[];
  details: Record<string, { score: number; max: number }>;
  processingTimeMs: number;
  processedAt: string;
  engine: string;
}

interface OcrResult {
  documentId: number;
  ocrData: OcrData;
}

interface TamperResponse {
  documentId: number;
  result: TamperResult;
}

interface OcrResultPanelProps {
  documentId: number;
  ocrData?: OcrData | null;
  verificationResult?: TamperResult | null;
  onOcrComplete?: () => void;
}

function fieldLabel(key: string): string {
  const labels: Record<string, string> = {
    fullName: 'Full Name',
    dateOfBirth: 'Date of Birth',
    gender: 'Gender',
    idNumber: 'ID Number',
    idType: 'ID Type',
    address: 'Address',
    nationality: 'Nationality',
    issueDate: 'Issue Date',
    expiryDate: 'Expiry Date',
  };
  return labels[key] || key;
}

function confidenceColor(confidence: number): string {
  if (confidence >= 80) return 'bg-emerald-50 text-emerald-700';
  if (confidence >= 50) return 'bg-amber-50 text-amber-700';
  return 'bg-red-50 text-red-700';
}

function confidenceBarColor(confidence: number): string {
  if (confidence >= 80) return 'bg-emerald-500';
  if (confidence >= 50) return 'bg-amber-500';
  return 'bg-red-500';
}

function severityColor(severity: string): string {
  if (severity === 'high') return 'bg-red-50 text-red-700 border-red-100';
  if (severity === 'medium') return 'bg-amber-50 text-amber-700 border-amber-100';
  return 'bg-stone-50 text-stone-600 border-stone-100';
}

function severityDot(severity: string): string {
  if (severity === 'high') return 'bg-red-500';
  if (severity === 'medium') return 'bg-amber-500';
  return 'bg-stone-400';
}

function tamperRiskColor(risk: string): string {
  if (risk === 'high') return 'bg-red-50 text-red-700 border-red-100';
  if (risk === 'medium') return 'bg-amber-50 text-amber-700 border-amber-100';
  return 'bg-emerald-50 text-emerald-700 border-emerald-100';
}

function tamperBarColor(score: number): string {
  if (score >= 70) return 'bg-red-500';
  if (score >= 40) return 'bg-amber-500';
  return 'bg-emerald-500';
}

export default function OcrResultPanel({ documentId, ocrData: initialOcr, verificationResult: initialTamper, onOcrComplete }: OcrResultPanelProps) {
  const { post } = useApi();
  const [ocrData, setOcrData] = useState<OcrData | null>(initialOcr || null);
  const [tamperResult, setTamperResult] = useState<TamperResult | null>(initialTamper || null);
  const [running, setRunning] = useState(false);
  const [runningTamper, setRunningTamper] = useState(false);
  const [error, setError] = useState('');
  const [tamperError, setTamperError] = useState('');
  const [showRaw, setShowRaw] = useState(false);
  const [activeTab, setActiveTab] = useState<'ocr' | 'tamper'>('ocr');

  const hasOcr = !!ocrData;
  const hasTamper = !!tamperResult;

  const handleRunOcr = useCallback(async () => {
    setRunning(true);
    setError('');
    try {
      const result: OcrResult = await post(`/documents/${documentId}/ocr`, {});
      setOcrData(result.ocrData);
      onOcrComplete?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'OCR failed');
    } finally {
      setRunning(false);
    }
  }, [documentId, post, onOcrComplete]);

  const handleRunTamper = useCallback(async () => {
    setRunningTamper(true);
    setTamperError('');
    try {
      const result: TamperResponse = await post(`/documents/${documentId}/tamper`, {});
      setTamperResult(result.result);
      onOcrComplete?.();
    } catch (err) {
      setTamperError(err instanceof Error ? err.message : 'Tamper scan failed');
    } finally {
      setRunningTamper(false);
    }
  }, [documentId, post, onOcrComplete]);

  const fields = ocrData?.fields;
  const extractedCount = fields
    ? Object.values(fields).filter((v) => v !== null && v !== '').length
    : 0;
  const totalFields = 9;

  return (
    <div className="bg-stone-50 rounded-xl border border-stone-100 p-4 space-y-4">
      {/* Header with tabs */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-brand-100 text-brand-700 flex items-center justify-center">
            <i className="ri-scan-line text-sm w-4 h-4 flex items-center justify-center" />
          </div>
          <div>
            <h4 className="text-sm font-semibold text-stone-900">AI Document Analysis</h4>
            <p className="text-stone-400 text-[11px]">
              {activeTab === 'ocr'
                ? (hasOcr ? `Extracted ${extractedCount}/${totalFields} fields` : 'OCR text extraction')
                : (hasTamper ? `Tamper score: ${tamperResult?.compositeScore}/100` : 'Forgery & tamper detection')}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1 bg-stone-100 rounded-full p-0.5">
          <button
            onClick={() => setActiveTab('ocr')}
            className={`px-3 py-1 rounded-full text-[11px] font-medium transition-colors ${
              activeTab === 'ocr' ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-500 hover:text-stone-700'
            }`}
          >
            OCR
          </button>
          <button
            onClick={() => setActiveTab('tamper')}
            className={`px-3 py-1 rounded-full text-[11px] font-medium transition-colors ${
              activeTab === 'tamper' ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-500 hover:text-stone-700'
            }`}
          >
            Tamper
          </button>
        </div>
      </div>

      {/* === OCR TAB === */}
      {activeTab === 'ocr' && (
        <>
          <div className="flex items-center justify-end">
            <button
              onClick={handleRunOcr}
              disabled={running}
              className="bg-brand-600 hover:bg-brand-700 disabled:bg-stone-300 text-white font-semibold px-3 py-1.5 rounded-full text-xs transition-colors flex items-center gap-1.5 whitespace-nowrap"
            >
              {running ? (
                <>
                  <i className="ri-loader-4-line animate-spin w-3 h-3 flex items-center justify-center" />
                  Scanning...
                </>
              ) : hasOcr ? (
                <>
                  <i className="ri-refresh-line w-3 h-3 flex items-center justify-center" />
                  Re-run OCR
                </>
              ) : (
                <>
                  <i className="ri-scan-line w-3 h-3 flex items-center justify-center" />
                  Run OCR
                </>
              )}
            </button>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-100 rounded-lg px-3 py-2 text-red-600 text-xs">
              {error}
            </div>
          )}

          {hasOcr && ocrData && (
            <>
              {/* Confidence bar */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-stone-500 text-[11px] font-medium">Confidence Score</span>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${confidenceColor(ocrData.confidence)}`}>
                    {ocrData.confidence}%
                  </span>
                </div>
                <div className="w-full h-2 bg-stone-200 rounded-full overflow-hidden">
                  <div
                    className={`h-full ${confidenceBarColor(ocrData.confidence)} rounded-full transition-all duration-500`}
                    style={{ width: `${ocrData.confidence}%` }}
                  />
                </div>
              </div>

              {/* Extracted fields */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {fields &&
                  Object.entries(fields).map(([key, value]) => (
                    <div
                      key={key}
                      className={`rounded-lg border px-3 py-2 ${
                        value ? 'border-stone-200 bg-white' : 'border-stone-100 bg-stone-50/50 opacity-60'
                      }`}
                    >
                      <p className="text-stone-400 text-[10px] uppercase tracking-wider">{fieldLabel(key)}</p>
                      <p className="text-stone-900 text-xs font-medium mt-0.5 truncate">
                        {value || '—'}
                      </p>
                    </div>
                  ))}
              </div>

              {/* Raw text toggle */}
              <div>
                <button
                  onClick={() => setShowRaw((s) => !s)}
                  className="text-brand-600 text-xs font-medium hover:underline flex items-center gap-1"
                >
                  <i className={`${showRaw ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} w-3 h-3 flex items-center justify-center`} />
                  {showRaw ? 'Hide Raw Text' : 'Show Raw OCR Text'}
                </button>
                {showRaw && (
                  <div className="mt-2 bg-white border border-stone-200 rounded-lg p-3 max-h-48 overflow-y-auto">
                    <pre className="text-[11px] text-stone-700 whitespace-pre-wrap font-mono leading-relaxed">
                      {ocrData.rawText}
                    </pre>
                  </div>
                )}
              </div>

              <p className="text-stone-300 text-[10px]">
                Processed on {new Date(ocrData.processedAt).toLocaleString()}
              </p>
            </>
          )}

          {!hasOcr && !running && !error && (
            <div className="text-center py-4">
              <i className="ri-file-search-line text-stone-300 text-xl w-8 h-8 flex items-center justify-center mx-auto mb-2" />
              <p className="text-stone-400 text-xs">No OCR data yet. Click "Run OCR" to extract text.</p>
            </div>
          )}
        </>
      )}

      {/* === TAMPER TAB === */}
      {activeTab === 'tamper' && (
        <>
          <div className="flex items-center justify-end">
            <button
              onClick={handleRunTamper}
              disabled={runningTamper}
              className="bg-stone-800 hover:bg-stone-900 disabled:bg-stone-300 text-white font-semibold px-3 py-1.5 rounded-full text-xs transition-colors flex items-center gap-1.5 whitespace-nowrap"
            >
              {runningTamper ? (
                <>
                  <i className="ri-loader-4-line animate-spin w-3 h-3 flex items-center justify-center" />
                  Analyzing...
                </>
              ) : hasTamper ? (
                <>
                  <i className="ri-refresh-line w-3 h-3 flex items-center justify-center" />
                  Re-run Tamper Scan
                </>
              ) : (
                <>
                  <i className="ri-shield-check-line w-3 h-3 flex items-center justify-center" />
                  Run Tamper Detection
                </>
              )}
            </button>
          </div>

          {tamperError && (
            <div className="bg-red-50 border border-red-100 rounded-lg px-3 py-2 text-red-600 text-xs">
              {tamperError}
            </div>
          )}

          {hasTamper && tamperResult && (
            <>
              {/* Risk summary */}
              <div className={`rounded-xl border p-3 flex items-center gap-3 ${tamperRiskColor(tamperResult.riskLevel)}`}>
                <div className="w-10 h-10 rounded-full bg-white/60 flex items-center justify-center shrink-0">
                  <i className={`${tamperResult.riskLevel === 'high' ? 'ri-alarm-warning-line' : tamperResult.riskLevel === 'medium' ? 'ri-error-warning-line' : 'ri-shield-check-line'} text-lg w-5 h-5 flex items-center justify-center`} />
                </div>
                <div>
                  <p className="text-sm font-bold">
                    {tamperResult.riskLevel === 'high' ? 'Suspicious — Possible Forgery' : tamperResult.riskLevel === 'medium' ? 'Caution — Some Anomalies Found' : 'Clean — No Tampering Detected'}
                  </p>
                  <p className="text-[11px] opacity-80">
                    Composite score {tamperResult.compositeScore}/100 · {tamperResult.flags.length} flag{tamperResult.flags.length !== 1 ? 's' : ''}
                  </p>
                </div>
              </div>

              {/* Composite score bar */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-stone-500 text-[11px] font-medium">Tamper Score</span>
                  <span className="text-stone-900 text-[11px] font-bold">{tamperResult.compositeScore}/100</span>
                </div>
                <div className="w-full h-2 bg-stone-200 rounded-full overflow-hidden">
                  <div
                    className={`h-full ${tamperBarColor(tamperResult.compositeScore)} rounded-full transition-all duration-500`}
                    style={{ width: `${tamperResult.compositeScore}%` }}
                  />
                </div>
                <p className="text-stone-400 text-[10px] mt-1">Higher score = more suspicious</p>
              </div>

              {/* Detail scores */}
              {tamperResult.details && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {Object.entries(tamperResult.details).map(([key, detail]) => (
                    <div key={key} className="bg-white border border-stone-200 rounded-lg px-2.5 py-2">
                      <p className="text-stone-400 text-[9px] uppercase tracking-wider">{key}</p>
                      <div className="flex items-center gap-1.5 mt-1">
                        <div className="flex-1 h-1.5 bg-stone-100 rounded-full overflow-hidden">
                          <div
                            className={`h-full ${tamperBarColor(detail.score)} rounded-full`}
                            style={{ width: `${detail.score}%` }}
                          />
                        </div>
                        <span className="text-stone-700 text-[10px] font-bold">{detail.score}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Flags list */}
              {tamperResult.flags.length > 0 && (
                <div className="space-y-2">
                  <p className="text-stone-500 text-[11px] font-medium">Detected Flags</p>
                  {tamperResult.flags.map((flag, idx) => (
                    <div
                      key={idx}
                      className={`flex items-center gap-2 rounded-lg border px-3 py-2 ${severityColor(flag.severity)}`}
                    >
                      <span className={`w-2 h-2 rounded-full shrink-0 ${severityDot(flag.severity)}`} />
                      <p className="text-xs font-medium">{flag.label}</p>
                      <span className="ml-auto text-[10px] uppercase tracking-wider opacity-70">{flag.severity}</span>
                    </div>
                  ))}
                </div>
              )}

              <p className="text-stone-300 text-[10px]">
                Scanned in {tamperResult.processingTimeMs}ms on {new Date(tamperResult.processedAt).toLocaleString()}
              </p>
            </>
          )}

          {!hasTamper && !runningTamper && !tamperError && (
            <div className="text-center py-4">
              <i className="ri-shield-check-line text-stone-300 text-xl w-8 h-8 flex items-center justify-center mx-auto mb-2" />
              <p className="text-stone-400 text-xs">No tamper scan yet. Click "Run Tamper Detection" to analyze.</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
